'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { ReactNode, MouseEvent, PointerEvent } from 'react';
import { cacheRecipeCard, type CachedRecipeCard } from '@/lib/utils/recipe-card-cache';
import { startHeroMorph } from '@/app/(public)/recipes/hero-morph';

interface RecipeCardLinkProps {
  href: string;
  slug: string;
  cacheData: CachedRecipeCard;
  className?: string;
  children: ReactNode;
}


/**
 * Link wrapper for a recipe card. On hover/click it stashes the card's
 * title + image in sessionStorage (so the recipe detail page's
 * OptimisticHero can render instantly). On click it also kicks off a
 * floating-image morph from the card's position to the eventual hero
 * position — see hero-morph.ts for why we do this manually instead of
 * using the View Transitions API.
 */
export function RecipeCardLink({ href, slug, cacheData, className, children }: RecipeCardLinkProps) {
  const router = useRouter();

  function onPointerEnter(_e: PointerEvent<HTMLAnchorElement>) {
    cacheRecipeCard(slug, cacheData);
    router.prefetch(href);
    // Production debugging hook — gated on a public env flag so we can flip
    // it on for a single deploy without re-deploying the rest of the app.
    if (process.env.NEXT_PUBLIC_DEBUG_PREFETCH === '1') {
      console.debug('[prefetch]', { slug, href });
    }
  }

  function onClick(e: MouseEvent<HTMLAnchorElement>) {
    cacheRecipeCard(slug, cacheData);

    // Modifier-click: let the browser open in a new tab/window. Don't morph.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    const card = e.currentTarget;
    const sourceEl = card.querySelector<HTMLElement>(`[data-morph-source="${cssEscape(slug)}"]`);
    if (!sourceEl) return;

    e.preventDefault();
    startHeroMorph({
      sourceRect: sourceEl.getBoundingClientRect(),
      imageUrl: cacheData.imageUrl,
      slug,
    });
    router.push(href);
  }

  return (
    <Link href={href} className={className} prefetch onPointerEnter={onPointerEnter} onClick={onClick}>
      {children}
    </Link>
  );
}

function cssEscape(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}
